import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";

import { ChevronLeft, ChevronRight, Play } from "lucide-react";

import CinemaVideoModal from "@/components/CinemaVideoModal";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { withIkVideoQuality } from "@/lib/utils/marketing";

export interface CaseStudyStat {
  value: string;
  label: string;
}

export interface CaseStudy {
  company: string;
  shortCompanyName?: string;
  name: string;
  role: string;
  backgroundImage: string;
  previewVideo?: string;
  videoUrl: string;
  stats: CaseStudyStat[];
  quote: string;
}

interface RealResultsSectionContent {
  badge: string;
  heading: React.ReactNode;
  storiesLabel: string;
}

interface RealResultsSectionProps {
  caseStudies: CaseStudy[];
  content: RealResultsSectionContent;
}

function BackgroundMedia({
  cs,
  isActive,
  priority,
  isInView,
}: {
  cs: CaseStudy;
  isActive: boolean;
  priority?: boolean;
  isInView: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const showVideo = !!cs.previewVideo && isVideoReady;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive && isInView) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive, isInView]);

  return (
    <>
      {cs.previewVideo && isInView && (
        <video
          ref={videoRef}
          src={withIkVideoQuality(cs.previewVideo ?? "")}
          loop
          muted
          playsInline
          onCanPlay={() => setIsVideoReady(true)}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-700",
            isActive && showVideo ? "opacity-20" : "opacity-0"
          )}
        />
      )}
      <Image
        src={cs.backgroundImage}
        alt={cs.company}
        fill
        className={cn(
          "object-cover transition-opacity duration-700",
          isActive && !showVideo ? "opacity-20" : "opacity-0"
        )}
        priority={priority}
      />
    </>
  );
}

function CaseStudyThumb({ cs, isActive, onClick }: { cs: CaseStudy; isActive: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative w-50 aspect-video rounded-xl overflow-hidden shrink-0 transition-all select-none",
        isActive ? "ring-1 ring-rebolt-500" : "ring-0 opacity-70 hover:opacity-100"
      )}
    >
      <Image src={cs.backgroundImage} alt={cs.company} fill className="object-cover opacity-20" />
      <div className="absolute inset-0 bg-ash-800/60" />
      <div className="absolute inset-0 flex items-center justify-center">
        <Play className="w-5 h-5 text-white fill-white" />
      </div>
      <div className="absolute bottom-0 left-0 bg-linear-to-t from-black/70 to-transparent p-3.5 pb-3 md:pb-3.5">
        <span className="text-white text-sm font-semibold whitespace-nowrap">{cs.shortCompanyName || cs.company}</span>
      </div>
    </button>
  );
}

export default function RealResultsSection({ caseStudies, content }: RealResultsSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const study = caseStudies[activeIndex];

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % caseStudies.length);
  }, [caseStudies.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);
  }, [caseStudies.length]);

  const openVideo = useCallback((cs: CaseStudy) => {
    if (!cs.videoUrl) return;
    setVideoUrl(cs.videoUrl);
    setVideoTitle(cs.company.replace("\n", " "));
    setVideoOpen(true);
  }, []);

  return (
    <div ref={sectionRef} className="w-full">
      <CinemaVideoModal open={videoOpen} onClose={() => setVideoOpen(false)} url={videoUrl} title={videoTitle} />

      <div className="relative w-full min-h-[560px] md:min-h-[700px] lg:h-[998px] overflow-hidden bg-ash-900">
        {/* background video / image fallback — deferred until section is near viewport */}
        <div className="absolute inset-0 blur-xs">
          {caseStudies.map((cs, i) => (
            <BackgroundMedia
              key={cs.company}
              cs={cs}
              isActive={i === activeIndex}
              priority={i === 0}
              isInView={isInView}
            />
          ))}
        </div>

        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1728 998" preserveAspectRatio="none">
          <defs>
            <radialGradient
              id="case-study-vignette"
              gradientUnits="userSpaceOnUse"
              cx="0"
              cy="0"
              r="10"
              gradientTransform="matrix(-1.5 63.691 -110.28 -2.467 864 499)"
            >
              <stop stopColor="rgba(23,23,23,0)" offset="0" />
              <stop stopColor="rgba(23,23,23,1)" offset="0.87938" />
            </radialGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#case-study-vignette)" />
        </svg>

        <div className="relative z-10 flex flex-col lg:flex-row max-w-screen-3xl mx-auto items-start justify-between h-full px-5 pb-6 py-12 md:px-16 md:py-16 lg:px-28 lg:py-24 gap-8 md:gap-10">
          {/* left column */}
          <div className="flex flex-col justify-between flex-1 gap-14 md:gap-10 lg:gap-0 lg:h-full w-full">
            <div className="flex flex-col gap-1 md:gap-4">
              <span className="text-rebolt-500 text-xs font-bold uppercase tracking-[1.15px]">{content.badge}</span>
              <h3 className="font-clash font-medium text-4xl lg:text-6xl leading-none tracking-tight">
                {content.heading}
              </h3>
            </div>

            {/* mobile play button */}
            <div className="flex lg:hidden items-center justify-center flex-1">
              <button
                onClick={() => openVideo(study)}
                className={cn(
                  "w-16 h-16 rounded-full bg-ash-600/60 border-2 border-ash-500 backdrop-blur-sm flex items-center justify-center hover:bg-ash-600/80 transition-colors",
                  !study.videoUrl && "opacity-50 cursor-not-allowed"
                )}
                disabled={!study.videoUrl}
              >
                <Play className="w-5 h-5 text-ash-100 fill-ash-100 ml-0.5" />
              </button>
            </div>

            {/* case study info */}
            <div className="flex flex-col gap-4 md:gap-6">
              <h4 className="font-clash font-semibold text-2xl md:text-4xl leading-none tracking-[-0.8px] text-white whitespace-pre-line">
                {study.company}
              </h4>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-white font-bold text-lg md:text-xl">{study.name}</span>
                  <span className="text-white/55 text-xs">{study.role}</span>
                </div>

                <div className="flex items-center md:gap-5 gap-4">
                  {study.stats.map((stat) => (
                    <div key={stat.label} className="flex flex-col gap-1">
                      <span className="text-white font-extrabold text-xl md:text-2xl tracking-[-1px]">
                        {stat.value}
                      </span>
                      <span className="text-clay-500 text-[11px]">{stat.label}</span>
                    </div>
                  ))}
                </div>

                <p className="text-white text-sm italic leading-relaxed max-w-md hidden md:block">{study.quote}</p>
              </div>
            </div>

            {/* nav arrows */}
            <div className="items-center gap-4 hidden md:flex">
              <button
                onClick={goPrev}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-ash-800 border border-ash-800 hover:bg-ash-700 transition-colors"
                aria-label="Previous case study"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={goNext}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-ash-800 border border-ash-800 hover:bg-ash-700 transition-colors"
                aria-label="Next case study"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* center play button (desktop) */}
          <div className="hidden lg:flex items-center justify-center absolute inset-0 pointer-events-none">
            <button
              onClick={() => openVideo(study)}
              className={cn(
                "pointer-events-auto w-18 h-18 rounded-full bg-ash-600/60 border-2 border-ash-500 backdrop-blur-sm flex items-center justify-center hover:bg-ash-600/80 transition-colors",
                !study.videoUrl && "opacity-50 cursor-not-allowed"
              )}
              disabled={!study.videoUrl}
            >
              <Play className="w-6 h-6 text-ash-100" />
            </button>
          </div>

          {/* right sidebar */}
          <div className="flex flex-col gap-3.5 md:gap-8 shrink-0 justify-center h-full w-full lg:w-auto">
            <span className="text-clay-300 text-xs font-bold uppercase tracking-[0.96px]">{content.storiesLabel}</span>

            {/* mobile/tablet: embla carousel */}
            <div className="lg:hidden -mx-4 md:-mx-16">
              <Carousel opts={{ align: "start", dragFree: true, containScroll: "trimSnaps" }}>
                <CarouselContent className="-ml-3 px-5 md:px-16">
                  {caseStudies.map((cs, i) => (
                    <CarouselItem key={cs.company} className="basis-auto pl-3">
                      <div className="py-1">
                        <CaseStudyThumb cs={cs} isActive={i === activeIndex} onClick={() => setActiveIndex(i)} />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>

            {/* desktop: vertical stack */}
            <div className="hidden lg:flex flex-col gap-8">
              {caseStudies.map((cs, i) => (
                <CaseStudyThumb
                  key={cs.company}
                  cs={cs}
                  isActive={i === activeIndex}
                  onClick={() => setActiveIndex(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
